import { useState, useEffect } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import { DEFAULT_REPORT_PRICING, type ReportPricing } from '../data/reportPricing';
import AdminLayout from '../components/AdminLayout';
import { Save, CheckCircle2, AlertTriangle, Info, Tag } from 'lucide-react';

/* Admin editor for the Capital Readiness report-unlock prices — Firestore `reportPricing/current`.
   p1 = "Report", p2 = "Report + advisory". Read live by CapitalAssessment + CapitalReadinessAssessment. */

const clone = (m: ReportPricing): ReportPricing => JSON.parse(JSON.stringify(m));
const inputCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-bold bg-[var(--brand-soft)] text-gray-900 focus:outline-none focus:border-[var(--brand)]';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

export default function AdminReportPricing() {
  const [m, setM] = useState<ReportPricing>(DEFAULT_REPORT_PRICING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'reportPricing', 'current'));
        if (snap.exists()) {
          const d = snap.data() as any;
          setM({
            AED: { p1: String(d?.AED?.p1 ?? DEFAULT_REPORT_PRICING.AED.p1), p2: String(d?.AED?.p2 ?? DEFAULT_REPORT_PRICING.AED.p2) },
            USD: { p1: String(d?.USD?.p1 ?? DEFAULT_REPORT_PRICING.USD.p1), p2: String(d?.USD?.p2 ?? DEFAULT_REPORT_PRICING.USD.p2) },
          });
        }
      } catch (e: any) {
        console.error('Failed to load reportPricing/current:', e);
        if (e?.code === 'permission-denied') setLoadError('Firestore permission denied: showing defaults. Sign in as a Firebase-authenticated admin to save.');
      } finally { setLoading(false); }
    })();
  }, []);

  const patch = (fn: (d: ReportPricing) => void) => { setM(prev => { const d = clone(prev); fn(d); return d; }); setSavedAt(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('reportPricing/current', { ...m });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'reportPricing/current');
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied' ? 'Save failed. Permission denied. This editor needs a Firebase-authenticated admin session.' : 'Save failed. Check console.');
    } finally { setSaving(false); }
  };
  const handleReset = () => { setM(clone(DEFAULT_REPORT_PRICING)); setSavedAt(null); };

  const LAYOUT = {
    section: 'Pricing',
    title: 'Report Unlock Pricing',
    icon: Tag,
    subtitle: 'Capital Readiness report unlock, live on the assessment paywall instantly.',
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading report pricing…" />;

  const Cur = ({ cur }: { cur: 'AED' | 'USD' }) => (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
      <div className="p-5">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{cur} prices</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Report (p1)</label>
            <div className="relative flex items-center"><span className="absolute left-3 text-gray-400 font-bold text-sm">{cur === 'AED' ? 'AED' : '$'}</span>
              <input value={m[cur].p1} onChange={e => patch(d => { d[cur].p1 = e.target.value; })} className={`${inputCls} ${cur === 'AED' ? 'pl-12' : 'pl-7'}`} /></div>
          </div>
          <div><label className={lbl}>Report + advisory (p2)</label>
            <div className="relative flex items-center"><span className="absolute left-3 text-gray-400 font-bold text-sm">{cur === 'AED' ? 'AED' : '$'}</span>
              <input value={m[cur].p2} onChange={e => patch(d => { d[cur].p2 = e.target.value; })} className={`${inputCls} ${cur === 'AED' ? 'pl-12' : 'pl-7'}`} /></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout {...LAYOUT} spaced>
      {loadError && (
        <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs font-bold text-red-800">
          <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" /><span>{loadError}</span>
        </div>
      )}
      <div className="flex gap-2 items-start bg-[var(--brand-soft)] border border-[var(--brand-line)] rounded-xl px-4 py-3 text-xs font-bold text-[var(--brand-d)]">
        <Info className="w-4 h-4 flex-none stroke-[var(--brand)] mt-0.5" />
        <span><b>p1</b> = Report only · <b>p2</b> = Report + advisory. Values are shown exactly as entered (e.g. <b>199</b>). The currency the user sees is auto-detected (AED in the Gulf, otherwise USD).</span>
      </div>

      <Cur cur="AED" />
      <Cur cur="USD" />

      <div className="flex items-center gap-3 flex-wrap pb-8">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[var(--brand)] hover:bg-[var(--brand-h)] disabled:opacity-60 text-[var(--brand-on)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0">
          <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save report pricing'}
        </button>
        <button onClick={handleReset} className="border border-gray-200 bg-white text-gray-600 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Reset to defaults</button>
        {savedAt && <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Saved · live as of {savedAt}</span>}
      </div>
    </AdminLayout>
  );
}
